import { FC, ReactNode, useState } from 'react'

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import { restrictToVerticalAxis, restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as ScrollArea from '@radix-ui/react-scroll-area'
import * as RadixSwitch from '@radix-ui/react-switch'
import { styled } from 'styled-components'

import Icon from 'core/components/Icon/Icon'
import Clickable from 'core/components/lib/Clickable'
import TextInput from 'core/components/lib/TextInput'
import {
  onSurface,
  onSurfaceVariant,
  outline,
  surface,
  surfaceBright,
  surfaceBrightHover,
  primary,
  primaryContainer,
  boxShadow,
} from 'core/styles'

import { ManagedColumn, UseColumnControlReturn } from './useColumnControl'

type SortableColumnItemProps = {
  id: string
  label: string
  isPinned: boolean
  isVisible: boolean
  isRequired: boolean
  onTogglePin: () => void
  onToggleVisibility: () => void
}

const SortableColumnItem: FC<SortableColumnItemProps> = ({
  id,
  label,
  // isPinned,
  isVisible,
  isRequired,
  // onTogglePin,
  onToggleVisibility,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 'auto',
  }

  return (
    <DragItemContainer ref={setNodeRef} style={style} {...attributes}>
      <DragHandle {...listeners} aria-label={`Drag ${label}`}>
        <Icon fontSize='20px' name='drag_indicator' />
      </DragHandle>
      <StyledItemLabel $isRequired={isRequired}>{label}</StyledItemLabel>
      <ItemActions>
        {/* <StyledIconButton
          ariaLabel={isPinned ? 'Unpin column' : 'Pin column'}
          color={onSurface}
          fontSize='20px'
          name={isPinned ? 'keep_off' : 'keep'}
          onClick={onTogglePin}
        /> */}
        <StyledSwitchRoot
          $disabled={isRequired}
          aria-label={isVisible ? 'Hide column' : 'Show column'}
          checked={isVisible}
          disabled={isRequired}
          id={`switch-${id}`}
          onCheckedChange={onToggleVisibility}
        >
          <StyledSwitchThumb />
        </StyledSwitchRoot>
      </ItemActions>
    </DragItemContainer>
  )
}

type ColumnSectionProps = {
  title: string
  children: ReactNode
  actionLabel?: string
  onAction?: () => void
}
const ColumnSection: FC<ColumnSectionProps> = ({ title, children, actionLabel, onAction }) => (
  <ColumnSectionContainer>
    <SectionTitleBox>
      <SectionTitle>{title}</SectionTitle>
      {actionLabel && (
        <ShowHideLink onClick={onAction} variant='link'>
          {actionLabel}
        </ShowHideLink>
      )}
    </SectionTitleBox>

    {children}
  </ColumnSectionContainer>
)

export type ColumnControlProps = Pick<
  UseColumnControlReturn,
  'orderedColumns' | 'setManagedColumnsList' | 'resetToDefault'
> & {
  hideLabel?: boolean
  menuAlignment?: 'start' | 'end'
}

const ColumnControl: FC<ColumnControlProps> = ({
  orderedColumns,
  setManagedColumnsList,
  resetToDefault,
  hideLabel = false,
  menuAlignment = 'start',
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const handleTogglePin = (labelToUpdate: string) => {
    const newColumnsList = orderedColumns.map((col) => {
      if (col.label === labelToUpdate) {
        const currentlyPinned = col.isPinned
        return { ...col, isPinned: !currentlyPinned, isVisible: currentlyPinned ? col.isVisible : true }
      }
      return col
    })
    setManagedColumnsList(newColumnsList)
  }

  const handleToggleVisibility = (labelToUpdate: string) => {
    const newColumnsList = orderedColumns.map((col) =>
      col.label === labelToUpdate ? { ...col, isVisible: !col.isVisible } : col,
    )
    setManagedColumnsList(newColumnsList, { bypassSort: true })
  }

  const handleShowAllUnpinnedColumns = () => {
    const newColumns: Array<ManagedColumn> = orderedColumns.map((col) => {
      if (!col.isPinned && !col.isRequired) {
        return { ...col, isVisible: true }
      }
      return col
    })
    setManagedColumnsList(newColumns)
  }

  const handleHideAllUnpinnedColumns = () => {
    const newColumns: Array<ManagedColumn> = orderedColumns.map((col) => {
      if (!col.isPinned && col.isVisible && !col.isRequired) {
        return { ...col, isVisible: false }
      }
      return col
    })
    setManagedColumnsList(newColumns)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const activeLabel = active.id as string
    const overLabel = over.id as string

    const activeColIndex = orderedColumns.findIndex((c) => c.label === activeLabel)
    const overColIndex = orderedColumns.findIndex((c) => c.label === overLabel)

    if (activeColIndex === -1 || overColIndex === -1) return

    const activeCol = orderedColumns[activeColIndex]
    const overCol = orderedColumns[overColIndex]

    if (activeCol.isPinned !== overCol.isPinned) {
      return
    }

    let reconstructedList = [...orderedColumns]

    if (activeCol.isPinned) {
      const currentPinned = orderedColumns.filter((c) => c.isPinned)
      const oldIdx = currentPinned.findIndex((c) => c.label === activeLabel)
      const newIdx = currentPinned.findIndex((c) => c.label === overLabel)
      if (oldIdx !== -1 && newIdx !== -1) {
        const movedInPinned = arrayMove(currentPinned, oldIdx, newIdx)
        const unpinned = orderedColumns.filter((c) => !c.isPinned)
        reconstructedList = [...movedInPinned, ...unpinned]
      }
    } else {
      const currentUnpinned = orderedColumns.filter((c) => !c.isPinned)
      const oldIdx = currentUnpinned.findIndex((c) => c.label === activeLabel)
      const newIdx = currentUnpinned.findIndex((c) => c.label === overLabel)
      if (oldIdx !== -1 && newIdx !== -1) {
        const movedInUnpinned = arrayMove(currentUnpinned, oldIdx, newIdx)
        const pinned = orderedColumns.filter((c) => c.isPinned)
        reconstructedList = [...pinned, ...movedInUnpinned]
      }
    }
    setManagedColumnsList(reconstructedList)
  }

  const displayableInMenuColumns = orderedColumns.filter((col) => col.label !== '')
  // const pinnedColumnsToRender = displayableInMenuColumns.filter((col) => col.isPinned)
  const unpinnedMenuColumns = displayableInMenuColumns.filter((col) => !col.isPinned)
  const unpinnedSearchedColumnsToRender = unpinnedMenuColumns.filter((col) =>
    col.label.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <DropdownMenu.Root onOpenChange={setIsMenuOpen} open={isMenuOpen}>
        <DropdownMenu.Trigger asChild>
          <StyledTrigger aria-label='Manage columns' variant='outline'>
            <Icon fontSize='24px' margin={`0 ${hideLabel ? '0' : '8px'} 0 0`} name='calendar_view_week' outlined />
            {!hideLabel && 'Columns'}
          </StyledTrigger>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <StyledDropdownContent align={menuAlignment} className='column-manager-content' sideOffset={5}>
            <StyledTextInput
              onChange={(value: string) => setSearchTerm(value.toLowerCase())}
              placeholder='Search column'
              prefix={<Icon name='search' />}
              value={searchTerm}
              variant='outline'
            />

            <StyledScrollArea>
              <StyledScrollAreaViewport>
                {/* Disabling pinned columns for now */}
                {/* {pinnedColumnsToRender.length > 0 && (
                  <ColumnSection title='Pinned'>
                    <SortableContext
                      items={pinnedColumnsToRender.map((c) => c.label)}
                      strategy={verticalListSortingStrategy}
                    >
                      {pinnedColumnsToRender.map((col) => (
                        <SortableColumnItem
                          id={col.label}
                          isPinned={col.isPinned}
                          isRequired={col.isRequired}
                          isVisible={col.isVisible}
                          key={col.label}
                          label={col.label}
                          onTogglePin={() => handleTogglePin(col.label)}
                          onToggleVisibility={() => handleToggleVisibility(col.label)}
                        />
                      ))}
                    </SortableContext>
                  </ColumnSection>
                )} */}

                <ColumnSection
                  actionLabel={
                    unpinnedSearchedColumnsToRender.some((c) => !c.isVisible && !c.isRequired) ? 'Show all'
                    : unpinnedSearchedColumnsToRender.some((c) => c.isVisible && !c.isRequired) ?
                      'Hide all'
                    : undefined
                  }
                  onAction={
                    unpinnedSearchedColumnsToRender.some((c) => !c.isVisible && !c.isRequired) ?
                      handleShowAllUnpinnedColumns
                    : unpinnedSearchedColumnsToRender.some((c) => c.isVisible && !c.isRequired) ?
                      handleHideAllUnpinnedColumns
                    : undefined
                  }
                  title='Display columns'
                >
                  {unpinnedSearchedColumnsToRender.length > 0 ?
                    <SortableContext
                      items={unpinnedSearchedColumnsToRender.map((c) => c.label)}
                      strategy={verticalListSortingStrategy}
                    >
                      {unpinnedSearchedColumnsToRender.map((col) => (
                        <SortableColumnItem
                          id={col.label}
                          isPinned={col.isPinned}
                          isRequired={col.isRequired}
                          isVisible={col.isVisible}
                          key={col.label}
                          label={col.label}
                          onTogglePin={() => handleTogglePin(col.label)}
                          onToggleVisibility={() => handleToggleVisibility(col.label)}
                        />
                      ))}
                    </SortableContext>
                  : <EmptySectionPlaceholder>No columns to display</EmptySectionPlaceholder>}
                </ColumnSection>
              </StyledScrollAreaViewport>
              <ScrollArea.Scrollbar orientation='vertical'>
                <ScrollArea.Thumb />
              </ScrollArea.Scrollbar>
            </StyledScrollArea>

            {resetToDefault && (
              <ResetButtonContainer>
                <ResetButton onClick={resetToDefault} variant='link'>
                  Reset to default
                </ResetButton>
              </ResetButtonContainer>
            )}
          </StyledDropdownContent>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </DndContext>
  )
}

export default ColumnControl

const StyledItemLabel = styled.span<{ $isRequired?: boolean }>`
  opacity: ${(props) => (props.$isRequired ? 0.7 : 1)};
  padding: 0 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${onSurface};
  font-size: 12px;
  font-weight: 600;
`

const StyledDropdownContent = styled(DropdownMenu.Content)`
  box-sizing: border-box;
  z-index: 100;
  border: 1px solid ${outline};
  border-radius: 8px;
  box-shadow: ${boxShadow};
  background-color: ${surfaceBright};
  padding: 16px 16px 10px;
  width: auto;
  min-width: 296px;
`

const StyledTextInput = styled(TextInput)`
  margin-bottom: 16px;
`

const ColumnSectionContainer = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionTitleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const SectionTitle = styled.div`
  opacity: 0.7;
  letter-spacing: 0.25px;
  color: ${onSurfaceVariant};
  font-size: 12px;
  font-weight: 800;
`

const DragItemContainer = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr auto;
  align-items: center;
  margin: 4px 0;
  border: 1px solid ${outline};
  border-radius: 8px;
  background-color: ${surface};
  padding: 8px;
`

// const StyledIconButton = styled(IconButton)<{ $disabled?: boolean }>`
//   opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
//   cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
//   padding: 0;
//   width: 20px;
//   height: 20px;
// `

const ItemActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
`

const StyledScrollArea = styled(ScrollArea.Root)`
  max-height: 360px;
  overflow: auto;
`

const StyledScrollAreaViewport = styled(ScrollArea.Viewport)`
  width: 100%;
  height: 100%;
`

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  width: 20px;
  height: 100%;
`

const ShowHideLink = styled(Clickable)`
  font-size: 12px;
  font-weight: 500;
`

const ResetButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0 8px;
`

const ResetButton = styled(Clickable)`
  font-size: 12px;
  font-weight: 500;
`

const EmptySectionPlaceholder = styled.div`
  padding: 8px;
  text-align: center;
  color: ${onSurfaceVariant};
  font-size: 12px;
  font-weight: 600;
`

const StyledTrigger = styled(Clickable)`
  font-size: 16px;
  font-weight: 600;

  &[data-state='open'] {
    background-color: ${surfaceBrightHover};
  }
`

const StyledSwitchRoot = styled(RadixSwitch.Root)<{ $disabled?: boolean }>`
  all: unset;
  position: relative;
  -webkit-tap-highlight-color: rgb(0 0 0 / 0%);
  border-radius: 9999px;
  box-shadow: 0 2px 10px ${outline};
  background-color: ${primaryContainer};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  width: 32px;
  height: 16px;

  &[data-state='checked'] {
    background-color: ${primary};
  }

  ${({ $disabled }) => ($disabled ? 'opacity: 0.6' : '1')};
`

const StyledSwitchThumb = styled(RadixSwitch.Thumb)`
  display: block;
  transform: translateX(2px);
  transition: transform 100ms;
  border-radius: 9999px;
  background-color: white;
  width: 14px;
  height: 14px;
  will-change: transform;

  &[data-state='checked'] {
    transform: translateX(17px);
  }
`
